import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, TrendingUp, IndianRupee, Target, FileText,
  BarChart3, PieChart, Calendar, Award, ArrowUpRight, ArrowDownRight,
  Briefcase, UserCheck, FileSpreadsheet, Clock, CheckCircle, AlertTriangle,
  CreditCard, Wallet, Receipt, Home, Database, Edit, Navigation, MapPin
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import BarterPipelinePanel from './BarterPipelinePanel';
import ReportIndexTab from './ReportIndexTab';
import CMDHRReportsPanel from './CMDHRReportsPanel';
import CMDFinancePanel from './CMDFinancePanel';
import CMDReceptionPanel from './CMDReceptionPanel';
import CMDMohineePanel from './CMDMohineePanel';
import CMDMarketingPanel from './CMDMarketingPanel';
import CMDChannelPanel from './CMDChannelPanel';
import FinanceDashboard from './FinanceDashboard';
import HRDashboard from './HRDashboard';
import MarketingDashboard from './MarketingDashboard';
import LeadsPage from './LeadsPage';
import CompanyPulseStrip from '../../components/CompanyPulseStrip';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// Super Admin department switcher — lets Aditya view & edit any dept's data
const DEPT_OPTIONS = [
  { key: 'marketing', label: 'All Marketing Leads (47K+)', icon: '🗂️' },
  { key: 'hr',        label: 'HR Dashboard',               icon: '👥' },
  { key: 'finance',   label: 'Finance Dashboard',          icon: '💰' },
];

const SuperAdminDeptTabs = ({ user }) => {
  const [activeDept, setActiveDept] = useState('leads');
  return (
    <div className="mt-3" data-testid="super-admin-dept-tabs">
      {/* Dept selector pills */}
      <div className="flex gap-2 flex-wrap mb-4">
        {DEPT_OPTIONS.map(d => (
          <button key={d.key}
            data-testid={`super-admin-tab-${d.key}`}
            onClick={() => setActiveDept(d.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 ${
              activeDept === d.key
                ? 'bg-purple-600 text-white shadow'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}>
            <span>{d.icon}</span> {d.label}
          </button>
        ))}
      </div>
      {/* Panel content */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        {activeDept === 'marketing'  && <MarketingDashboard user={user} />}
        {activeDept === 'hr'         && <HRDashboard user={user} />}
        {activeDept === 'finance'    && <FinanceDashboard user={user} />}
      </div>
    </div>
  );
};

const CMDDashboard = ({ user }) => {
  const [stats, setStats] = useState({
    totalSales: 0,
    pendingPayments: 0,
    dailyCollection: 0,
    marketingBusiness: 0,
    totalBookings: 0,
    recoveryPct: 0
  });
  const [cpPerformance, setCpPerformance] = useState([]);
  const [dmPerformance, setDmPerformance] = useState([]);
  const [segments, setSegments] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [fieldStats, setFieldStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCMDData();
    fetchFieldStats();
  }, []);

  const fetchCMDData = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch CMD dashboard data from finance API
      const cmdDataRes = await axios.get(`${API}/finance/cmd-dashboard-data`, { headers }).catch(() => ({ data: {} }));
      const cmdData = cmdDataRes.data || {};

      setStats({
        totalSales: cmdData.totalSales || 0,
        pendingPayments: cmdData.pendingPayments || 0,
        dailyCollection: cmdData.dailyCollection || 0,
        marketingBusiness: cmdData.marketingBusiness || 0,
        totalBookings: cmdData.totalBookings || 0,
        recoveryPct: cmdData.recoveryPct || 0
      });

      setCpPerformance(cmdData.cpPerformance || []);
      setDmPerformance(cmdData.dmPerformance || []);
      setSegments(cmdData.segments || []);
      setMonthlyData(cmdData.monthlyData || []);

      // Fetch users for department stats
      const usersRes = await axios.get(`${API}/crm/admin/users`, { headers }).catch(() => ({ data: [] }));
      const users = usersRes.data || [];

      // Calculate department stats
      const depts = [
        { key: 'admin', name: 'Admin', icon: Briefcase, color: 'bg-blue-500' },
        { key: 'hr', name: 'HR', icon: Users, color: 'bg-purple-500' },
        { key: 'finance', name: 'Finance', icon: IndianRupee, color: 'bg-emerald-500' },
        { key: 'marketing', name: 'Marketing', icon: TrendingUp, color: 'bg-pink-500' },
        { key: 'legal', name: 'Legal', icon: FileText, color: 'bg-amber-500' },
        { key: 'operations', name: 'Operations', icon: Building2, color: 'bg-cyan-500' },
        { key: 'architect', name: 'Architect', icon: Home, color: 'bg-indigo-500' },
        { key: 'it', name: 'IT', icon: Building2, color: 'bg-teal-500' },
        { key: 'dm_channel', name: 'DM Channel', icon: Target, color: 'bg-orange-500' },
      ];

      const deptStats = depts.map(dept => {
        const deptUsers = users.filter(u => u.department === dept.key);
        return {
          ...dept,
          employees: deptUsers.length,
          activeEmployees: deptUsers.filter(u => u.status === 'active').length
        };
      });

      setDepartmentStats(deptStats);

    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const fetchFieldStats = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const { data } = await axios.get(`${API}/location/field-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFieldStats(data);
    } catch {
      // CMD may not have field-stats access in edge cases — fail silently
      setFieldStats(null);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(2)} K`;
    return `₹${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Data for collections-by-segment pie chart
  const projectPieData = (segments || []).map(s => ({
    name: s.name || 'Unknown',
    value: s.value || 0
  }));

  return (
    <div className="space-y-6" data-testid="cmd-dashboard">
      {/* Welcome Banner */}
      <Card className="bg-gradient-to-r from-stone-800 to-stone-900 text-white border-0">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-stone-400 text-sm">Chairman & Managing Director</p>
              <h2 className="text-2xl font-bold mt-1">Welcome, Mr. {user?.firstName} {user?.lastName}!</h2>
              <p className="text-stone-300 mt-2">DBK Group Of Industries - Executive Dashboard</p>
            </div>
            <div className="text-right">
              <p className="text-stone-400 text-sm">Today's Date</p>
              <p className="text-lg font-medium">{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Pulse Strip */}
      <CompanyPulseStrip />

      {/* Key Financial Metrics */}
      <div>
        <p className="text-[11px] font-semibold text-stone-400 uppercase tracking-widest mb-3 flex items-center gap-2">
          <span className="w-4 h-px bg-stone-300 inline-block" />
          Key Financial Metrics
          <span className="flex-1 h-px bg-stone-200 inline-block" />
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0 shadow-md">
            <CardContent className="p-4">
              <IndianRupee className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-2xl font-bold">{formatCurrency(stats.totalSales)}</p>
              <p className="text-xs text-emerald-100">Total Sales</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0 shadow-md">
            <CardContent className="p-4">
              <Wallet className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-2xl font-bold">{formatCurrency(stats.pendingPayments)}</p>
              <p className="text-xs text-red-100">Pending Payments</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-md">
            <CardContent className="p-4">
              <Receipt className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-2xl font-bold">{formatCurrency(stats.dailyCollection)}</p>
              <p className="text-xs text-blue-100">Total Collected</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-md">
            <CardContent className="p-4">
              <CreditCard className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-2xl font-bold">{formatCurrency(stats.marketingBusiness)}</p>
              <p className="text-xs text-purple-100">Marketing Business</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0 shadow-md">
            <CardContent className="p-4">
              <Home className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-2xl font-bold">{stats.totalBookings}</p>
              <p className="text-xs text-amber-100">Total Bookings</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white border-0 shadow-md">
            <CardContent className="p-4">
              <CheckCircle className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-2xl font-bold">{stats.recoveryPct}%</p>
              <p className="text-xs text-cyan-100">Recovery %</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Field Activity Today — Channel Teams */}
      {fieldStats && (
        <div>
          <p className="text-[11px] font-semibold text-stone-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <span className="w-4 h-px bg-stone-300 inline-block" />
            Field Operations — Channel Teams
            <span className="flex-1 h-px bg-stone-200 inline-block" />
          </p>
          <Card data-testid="field-activity-card" className="border border-slate-700/50 bg-gradient-to-br from-slate-800 to-slate-900 text-white shadow-xl">
            <CardHeader className="pb-3 border-b border-white/10">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                    <Navigation className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <CardTitle className="text-base text-white">Field Activity Today</CardTitle>
                    <p className="text-xs text-slate-400 mt-0.5">Live tracking · {fieldStats.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-400 leading-none">{fieldStats.totalInField}</p>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">In Field</p>
                  </div>
                  <div className="w-px h-10 bg-slate-600" />
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-400 leading-none">{fieldStats.totalAtOffice}</p>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">At Office</p>
                  </div>
                  <div className="w-px h-10 bg-slate-600" />
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-300 leading-none">{fieldStats.totalTracked}</p>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Tracked</p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {Object.entries(fieldStats.departments || {}).map(([key, dept]) => (
                  <div key={key}
                    className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-4 hover:bg-white/[0.07] transition-colors"
                    data-testid={`field-stat-${key}`}>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-bold text-slate-200 tracking-wide">{dept.label}</p>
                      <span className="text-[10px] text-slate-500 bg-white/5 px-2 py-0.5 rounded-full">{dept.total} total</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-white/5 rounded-xl p-2.5 text-center">
                        <p className="text-xl font-bold text-orange-400 leading-none">{dept.inField}</p>
                        <p className="text-[10px] text-slate-500 mt-1">Field</p>
                      </div>
                      <div className="flex-1 bg-white/5 rounded-xl p-2.5 text-center">
                        <p className="text-xl font-bold text-emerald-400 leading-none">{dept.atOffice}</p>
                        <p className="text-[10px] text-slate-500 mt-1">Office</p>
                      </div>
                    </div>
                    {dept.total > 0 && (
                      <div className="mt-3">
                        <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                          <span>Field coverage</span>
                          <span>{Math.round((dept.inField / dept.total) * 100)}%</span>
                        </div>
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all"
                            style={{ width: `${Math.round((dept.inField / dept.total) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-end gap-1.5 mt-3 pt-3 border-t border-white/5">
                <MapPin className="w-3 h-3 text-slate-500" />
                <p className="text-[10px] text-slate-500">Geofence: Akurdi HQ ≤ 500 m = "At Office"</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Collection Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              Monthly Collection Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Area isAnimationActive={false} type="monotone" dataKey="collection" stroke="#10b981" fill="#10b98133" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-stone-400">
                  No data available yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Project Bookings Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-600" />
              Collections by Segment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              {projectPieData.length > 0 && projectPieData.some(p => p.value > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie isAnimationActive={false}
                      data={projectPieData.filter(p => p.value > 0)}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                    >
                      {projectPieData.filter(p => p.value > 0).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-stone-400">
                  No segment data yet
                </div>
              )}
            </div>
            {/* Clear value legend so every segment (even tiny ones) is readable */}
            <div className="mt-3 space-y-2" data-testid="segment-legend">
              {projectPieData.map((entry, index) => {
                const total = projectPieData.reduce((s, p) => s + (p.value || 0), 0);
                const pctVal = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0.0';
                return (
                  <div key={entry.name} className="flex items-center justify-between text-sm" data-testid={`segment-row-${entry.name}`}>
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                      <span className="font-medium text-stone-700">{entry.name}</span>
                    </span>
                    <span className="font-bold text-stone-900">{formatCurrency(entry.value)} <span className="text-stone-400 font-normal">({pctVal}%)</span></span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CP/DM/ADM Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CP Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              Top CP Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cpPerformance.length > 0 ? (
              <div className="space-y-3">
                {cpPerformance.slice(0, 5).map((cp, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 flex items-center justify-center rounded-full text-white font-bold ${
                        idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-stone-400' : idx === 2 ? 'bg-amber-700' : 'bg-stone-300'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium">{cp.name}</p>
                        <p className="text-xs text-stone-500">{cp.totalBookings} bookings</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600">{formatCurrency(cp.totalValue)}</p>
                      <p className="text-xs text-stone-500">Collected: {formatCurrency(cp.collectedAmount)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-400">
                No CP performance data yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* DM Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Top DM Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dmPerformance.length > 0 ? (
              <div className="space-y-3">
                {dmPerformance.slice(0, 5).map((dm, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 flex items-center justify-center rounded-full text-white font-bold ${
                        idx === 0 ? 'bg-blue-500' : idx === 1 ? 'bg-stone-400' : idx === 2 ? 'bg-blue-700' : 'bg-stone-300'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium">{dm.name}</p>
                        <p className="text-xs text-stone-500">{dm.totalBookings} bookings</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-600">{formatCurrency(dm.totalValue)}</p>
                      <p className="text-xs text-stone-500">Collected: {formatCurrency(dm.collectedAmount)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-400">
                No DM performance data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* HR Department — All Reports (percentage-wise) */}
      <CMDHRReportsPanel />

      {/* DM/ADM/CP Channel Drilldown — CMD can view & operate any account */}
      <CMDChannelPanel user={user} />

      {/* Barter Department Pipeline */}
      <BarterPipelinePanel />

      {/* Finance Department — Snehal Katkar (percentage-wise) */}
      <CMDFinancePanel />

      {/* Finance — full executive sheets (view / edit / import) */}
      <Card data-testid="cmd-finance-sheets">
        <CardContent className="p-4 sm:p-6">
          <FinanceDashboard user={user} embedded />
        </CardContent>
      </Card>

      {/* Reception — Hetal Mandalik (percentage-wise) */}
      <CMDReceptionPanel />

      {/* Reception 2 — Nayana Mahajan (percentage-wise) */}
      <CMDMohineePanel />

      {/* Marketing & Digital Marketing (percentage-wise) */}
      <CMDMarketingPanel />

      {/* ── Super Admin CRM Data Panel (only for super_admin / Aditya) ── */}
      {user?.role === 'super_admin' && (
        <Card data-testid="super-admin-crm-panel">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Database className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-base">Super Admin — CRM Data Access</CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">View and edit any department's data directly</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <SuperAdminDeptTabs user={user} />
          </CardContent>
        </Card>
      )}

      {/* All Staff + DM/ADM/CP Index 1 & 2 Checklist */}
      <Card data-testid="cmd-index-panel">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <FileText className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-base">All Staff Index 1 &amp; 2</CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">View &amp; manage daily checklist for all 42 staff employees, DM, ADM, and CP</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 pb-4">
          <ReportIndexTab user={user} isHR={true} startInAllView={true} />
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" className="flex items-center gap-2" onClick={() => window.location.href='/dashboard/users'}>
              <Users className="w-4 h-4" />
              Manage Users
            </Button>
            <Button variant="outline" className="flex items-center gap-2" onClick={() => window.location.href='/dashboard/analytics'}>
              <BarChart3 className="w-4 h-4" />
              Analytics
            </Button>
            <Button variant="outline" className="flex items-center gap-2" onClick={() => window.location.href='/dashboard/leads'}>
              <Target className="w-4 h-4" />
              Leads
            </Button>
            <Button variant="outline" className="flex items-center gap-2" onClick={() => window.location.href='/dashboard/pipeline'}>
              <TrendingUp className="w-4 h-4" />
              Sales Pipeline
            </Button>
            <Button variant="outline" className="flex items-center gap-2" onClick={() => window.location.href='/dashboard/excel'}>
              <FileSpreadsheet className="w-4 h-4" />
              Excel Reports
            </Button>
            <Button variant="outline" className="flex items-center gap-2" onClick={() => window.location.href='/dashboard/contests'}>
              <Award className="w-4 h-4" />
              Contests
            </Button>
            <Button variant="outline" className="flex items-center gap-2" onClick={() => window.location.href='/dashboard/hierarchy'}>
              <Building2 className="w-4 h-4" />
              DM Hierarchy
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CMDDashboard;
